import React, { useEffect, useMemo, useRef, useState } from 'react';
import Icon from '@/components/Icon';

/**
 * Чип-фильтр диапазона дат с пресетами:
 *   • Сегодня
 *   • Вчера
 *   • Последние 7 дней
 *   • Последние 30 дней
 *   • В этом месяце
 *   • В прошлом месяце
 *   • Задать диапазон  (раскрывает два <input type="date">)
 *
 * Props:
 *   from, to (string, YYYY-MM-DD) — текущий диапазон
 *   onChange({ from, to }) — отдаёт новый диапазон
 */
const PRESETS = [
  { v: 'today',           label: 'Сегодня' },
  { v: 'yesterday',       label: 'Вчера' },
  { v: 'last_7_days',     label: 'Последние 7 дней' },
  { v: 'last_30_days',    label: 'Последние 30 дней' },
  { v: 'this_month',      label: 'Этот месяц' },
  { v: 'prev_month',      label: 'Прошлый месяц' },
  { v: 'prev_prev_month', label: 'Позапрошлый месяц' },
];

/* ---- helpers ---- */
function pad(n) { return String(n).padStart(2, '0'); }
function isoOf(d) {
  return d.getFullYear() + '-' + pad(d.getMonth() + 1) + '-' + pad(d.getDate());
}
function parseIso(s) {
  if (!s) return null;
  const [y, m, d] = s.split('-').map((x) => parseInt(x, 10));
  return new Date(y, m - 1, d);
}
function addDays(d, n) {
  const x = new Date(d);
  x.setDate(x.getDate() + n);
  return x;
}
function startOfMonth(d) {
  return new Date(d.getFullYear(), d.getMonth(), 1);
}
function endOfMonth(d) {
  return new Date(d.getFullYear(), d.getMonth() + 1, 0);
}
function fmtDateRu(iso) {
  const d = parseIso(iso);
  if (!d) return '';
  return pad(d.getDate()) + '.' + pad(d.getMonth() + 1) + '.' + d.getFullYear();
}

export function rangeForPreset(preset) {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  switch (preset) {
    case 'today':
      return { from: isoOf(today), to: isoOf(today) };
    case 'yesterday': {
      const y = addDays(today, -1);
      return { from: isoOf(y), to: isoOf(y) };
    }
    case 'last_7_days':
      return { from: isoOf(addDays(today, -6)), to: isoOf(today) };
    case 'last_30_days':
      return { from: isoOf(addDays(today, -29)), to: isoOf(today) };
    case 'this_month':
      return { from: isoOf(startOfMonth(today)), to: isoOf(endOfMonth(today)) };
    case 'prev_month': {
      const prev = new Date(today.getFullYear(), today.getMonth() - 1, 1);
      return { from: isoOf(startOfMonth(prev)), to: isoOf(endOfMonth(prev)) };
    }
    case 'prev_prev_month': {
      const prev = new Date(today.getFullYear(), today.getMonth() - 2, 1);
      return { from: isoOf(startOfMonth(prev)), to: isoOf(endOfMonth(prev)) };
    }
    default:
      return null;
  }
}

/** Угадать пресет по from/to. Если не подходит ни один — null (custom). */
function detectPreset(from, to, presetList = PRESETS) {
  if (!from || !to) return null;
  for (const p of presetList) {
    const r = rangeForPreset(p.v);
    if (r && r.from === from && r.to === to) return p.v;
  }
  return null;
}

/**
 * @param {Object}   props
 * @param {string}   props.from
 * @param {string}   props.to
 * @param {Function} props.onChange   { from, to }
 * @param {string[]} [props.presets]  список ключей пресетов (today, prev_month, ...).
 *                                    Если не указан — показываем все.
 */
export default function DateRangePicker({ from, to, onChange, presets }) {
  // Если caller передал свой список пресетов — фильтруем по нему.
  const presetList = useMemo(() => {
    if (!Array.isArray(presets) || presets.length === 0) return PRESETS;
    const set = new Set(presets);
    return PRESETS.filter((p) => set.has(p.v));
  }, [presets]);

  const [open, setOpen]     = useState(false);
  const [custom, setCustom] = useState(false);
  const [draftFrom, setDraftFrom] = useState(from || '');
  const [draftTo, setDraftTo]     = useState(to || '');
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    const close = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [open]);

  // Если props from/to меняются (например, пресет применён извне) — синхронизируем.
  useEffect(() => { setDraftFrom(from || ''); setDraftTo(to || ''); }, [from, to]);

  const currentPreset = useMemo(() => detectPreset(from, to, presetList), [from, to, presetList]);
  const summary = (() => {
    if (currentPreset) {
      const o = presetList.find((p) => p.v === currentPreset);
      return o ? o.label : '';
    }
    if (from && to) return fmtDateRu(from) + ' – ' + fmtDateRu(to);
    if (from)       return 'с ' + fmtDateRu(from);
    if (to)         return 'по ' + fmtDateRu(to);
    return 'все даты';
  })();

  const isActive = !!(from || to);

  const applyPreset = (v) => {
    const r = rangeForPreset(v);
    if (r) {
      onChange(r);
      setOpen(false);
      setCustom(false);
    }
  };

  const applyCustom = () => {
    if (!draftFrom || !draftTo) return;
    onChange({ from: draftFrom, to: draftTo });
    setOpen(false);
    setCustom(false);
  };

  return (
    <div className="chip-wrap" ref={ref}>
      <button
        type="button"
        className={'chip' + (isActive ? ' is-active' : '')}
        onClick={() => setOpen((o) => !o)}
      >
        <Icon name="calendar" size={12} />
        <span className="chip__label">{summary}</span>
        <Icon name="chevronDown" size={10} />
      </button>
      {open && (
        <div className="menu" style={{ minWidth: 220 }}>
          {presetList.map((p) => {
            const active = currentPreset === p.v;
            return (
              <button
                key={p.v}
                type="button"
                className={'menu__item' + (active ? ' is-active' : '')}
                onClick={() => applyPreset(p.v)}
              >
                <span>{p.label}</span>
                {active && <Icon name="check" size={12} />}
              </button>
            );
          })}
          <div style={{ height: 1, background: 'var(--border)', margin: '4px 0' }} />
          {!custom ? (
            <button
              type="button"
              className={'menu__item' + (currentPreset === null && isActive ? ' is-active' : '')}
              onClick={() => setCustom(true)}
            >
              <span>Задать диапазон…</span>
            </button>
          ) : (
            <div style={{ padding: '6px 8px', display: 'flex', flexDirection: 'column', gap: 6 }}>
              <label style={{ fontSize: 11, color: 'var(--fg-muted)' }}>С</label>
              <input
                type="date"
                value={draftFrom}
                onChange={(e) => setDraftFrom(e.target.value)}
                className="chip-date"
                style={{ width: '100%' }}
              />
              <label style={{ fontSize: 11, color: 'var(--fg-muted)' }}>По</label>
              <input
                type="date"
                value={draftTo}
                onChange={(e) => setDraftTo(e.target.value)}
                className="chip-date"
                style={{ width: '100%' }}
              />
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 6, marginTop: 4 }}>
                <button className="btn btn--sm" type="button" onClick={() => setCustom(false)}>
                  Отмена
                </button>
                <button
                  className="btn btn--primary btn--sm"
                  type="button"
                  onClick={applyCustom}
                  disabled={!draftFrom || !draftTo}
                >
                  Применить
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
